// Shared factory data store — backed by Supabase
// All functions are async; components should await them or use the subscribe pattern.

import { supabase } from '../lib/supabase';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const toApp = (row) => ({
  id: row?.id,
  shortCode: row?.short_code || '',
  name: row?.name,
  location: row?.location || '',
  state: row?.state || '',
  pincode: row?.pincode || '',
  status: row?.status || 'Active',
  productDescription: row?.product_description || '',
  grade: row?.grade || '',
  zone: row?.zone || '',
  city: row?.city || '',
  bestRoute: row?.best_route || '',
  estimatedTravelTime: row?.estimated_travel_time || '',
  latitude: row?.latitude ?? null,
  longitude: row?.longitude ?? null,
  factoryPhotoUrl: row?.factory_photo_url || '',
  productPhotoUrl: row?.product_photo_url || '',
  packingPhotoUrl: row?.packing_photo_url || '',
  reviews: row?.reviews || [],
});

const toDb = (app) => ({
  short_code: app?.shortCode || null,
  name: app?.name,
  location: app?.location || '',
  state: app?.state || '',
  pincode: app?.pincode || '',
  status: app?.status || 'Active',
  product_description: app?.productDescription || '',
  grade: app?.grade || '',
  zone: app?.zone || '',
  city: app?.city || '',
  best_route: app?.bestRoute || '',
  estimated_travel_time: app?.estimatedTravelTime || '',
  latitude: app?.latitude ?? null,
  longitude: app?.longitude ?? null,
  factory_gstin: app?.factoryGstin || '',
  factory_photo_url: app?.factoryPhotoUrl || '',
  product_photo_url: app?.productPhotoUrl || '',
  packing_photo_url: app?.packingPhotoUrl || '',
  reviews: app?.reviews || [],
});

// ─── Listeners (for real-time UI updates) ─────────────────────────────────────
let listeners = [];

const notifyListeners = (data) => {
  listeners?.forEach((fn) => fn(data));
};

export const subscribeFactories = (fn) => {
  listeners?.push(fn);

  // Immediately fetch and notify
  getFactories()?.then((data) => fn(data));

  // Subscribe to Supabase real-time changes
  const channel = supabase?.channel('factories_changes')?.on('postgres_changes', { event: '*', schema: 'public', table: 'factories' }, async () => {
      const updated = await getFactories();
      notifyListeners(updated);
    })?.subscribe();

  return () => {
    listeners = listeners?.filter((l) => l !== fn);
    supabase?.removeChannel(channel);
  };
};

// ─── CRUD ─────────────────────────────────────────────────────────────────────
export const getFactories = async () => {
  const { data, error } = await supabase?.from('factories')?.select('*')?.order('created_at', { ascending: true });
  if (error) {
    console.error('getFactories error:', error?.message);
    return [];
  }
  return (data || [])?.map(toApp);
};

export const getActiveFactories = async () => {
  const { data, error } = await supabase?.from('factories')?.select('*')?.eq('status', 'Active')?.order('name', { ascending: true });
  if (error) {
    console.error('getActiveFactories error:', error?.message);
    return [];
  }
  return (data || [])?.map(toApp);
};

export const addFactory = async (factory) => {
  const { data, error } = await supabase?.from('factories')?.insert(toDb(factory))?.select()?.single();
  if (error) {
    console.error('addFactory error:', error?.message);
    return null;
  }
  const newFactory = toApp(data);
  const all = await getFactories();
  notifyListeners(all);
  return newFactory;
};

export const updateFactory = async (shortCode, updates) => {
  const dbUpdates = toDb({ shortCode, ...updates });
  const { error } = await supabase?.from('factories')?.update(dbUpdates)?.eq('short_code', shortCode);
  if (error) {
    console.error('updateFactory error:', error?.message);
    return;
  }
  const all = await getFactories();
  notifyListeners(all);
};

export const updateFactoryById = async (id, updates) => {
  const dbUpdates = toDb(updates);
  const { error } = await supabase?.from('factories')?.update(dbUpdates)?.eq('id', id);
  if (error) {
    console.error('updateFactoryById error:', error?.message);
    return;
  }
  const all = await getFactories();
  notifyListeners(all);
};

export const removeFactory = async (id) => {
  const { error } = await supabase?.from('factories')?.delete()?.eq('id', id);
  if (error) {
    console.error('removeFactory error:', error?.message);
    return;
  }
  const all = await getFactories();
  notifyListeners(all);
};

// Bulk upsert — used by CSV upload
export const upsertFactories = async (factoryList) => {
  const rows = factoryList?.map((f) => ({
    ...toDb(f),
    ...(f?.id && typeof f?.id === 'string' && f?.id?.includes('-') ? { id: f?.id } : {}),
  }));

  const { error } = await supabase?.from('factories')?.upsert(rows, { onConflict: 'short_code', ignoreDuplicates: false });

  if (error) {
    console.error('upsertFactories error:', error?.message);
    return;
  }
  const all = await getFactories();
  notifyListeners(all);
};

// Insert a single new factory row (for CSV rows without an existing short_code)
export const insertFactory = async (factory) => {
  const { data, error } = await supabase?.from('factories')?.insert(toDb(factory))?.select()?.single();
  if (error) {
    console.error('insertFactory error:', error?.message);
    return null;
  }
  const all = await getFactories();
  notifyListeners(all);
  return toApp(data);
};

